import { useEffect, useRef, useState } from "react";
import { onShowPdf } from "@/lib/pdfViewer";
import { Button } from "@/components/ui/button";
import { X, Printer, DownloadSimple, ArrowSquareOut } from "@phosphor-icons/react";

/**
 * Uygulama içi PDF önizleme modalı.
 * - previewPdf() blob URL üretince showPdf() event'i dispatch eder.
 * - Bu portal event'i dinler ve fixed konumlu, üstünde toolbar olan
 *   bir <iframe src={blobUrl}> gösterir. Popup blocker yok.
 * - Kullanıcı Yazdır ile iframe içeriğini print eder, İndir ile dosyayı
 *   diske kaydeder, Kapat ile modalı kapatır.
 */
export default function PdfViewerPortal() {
  const [state, setState] = useState(null); // { url, filename } | null
  const iframeRef = useRef(null);

  useEffect(() => {
    return onShowPdf((detail) => setState(detail));
  }, []);

  useEffect(() => {
    if (!state) return;
    const onKey = (e) => { if (e.key === "Escape") setState(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state]);

  if (!state) return null;

  const handlePrint = () => {
    try {
      const w = iframeRef.current?.contentWindow;
      w?.focus();
      w?.print();
    } catch {
      window.open(state.url, "_blank");
    }
  };

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = state.url;
    a.download = state.filename || "belge.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div
      data-testid="pdf-viewer-portal"
      style={{
        position: "fixed", inset: 0, background: "rgba(20,20,20,0.85)",
        zIndex: 9999, display: "flex", flexDirection: "column",
      }}
    >
      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
        background: "#1f1f1f", color: "#fff", flex: "0 0 auto",
      }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginRight: "auto",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {state.filename || "PDF Önizleme"}
        </div>
        <Button size="sm" variant="secondary" onClick={handlePrint}
                data-testid="pdf-viewer-print-btn">
          <Printer size={16} weight="bold" className="mr-1" /> Yazdır
        </Button>
        <Button size="sm" variant="secondary" onClick={handleDownload}
                data-testid="pdf-viewer-download-btn">
          <DownloadSimple size={16} weight="bold" className="mr-1" /> İndir
        </Button>
        <Button size="sm" variant="secondary"
                onClick={() => window.open(state.url, "_blank", "noopener")}
                data-testid="pdf-viewer-open-tab-btn"
                title="Yeni sekmede aç (iframe'de görünmezse)">
          <ArrowSquareOut size={16} weight="bold" className="mr-1" /> Yeni Sekme
        </Button>
        <Button size="sm" variant="destructive" onClick={() => setState(null)}
                data-testid="pdf-viewer-close-btn">
          <X size={16} weight="bold" className="mr-1" /> Kapat
        </Button>
      </div>
      <iframe
        ref={iframeRef}
        src={state.url}
        title={state.filename || "PDF"}
        style={{ flex: 1, border: 0, background: "#525659", width: "100%" }}
        data-testid="pdf-viewer-iframe"
      />
    </div>
  );
}
